import { useAuthStore } from "~/store/auth/auth.store";
import { isOidcRoute, OidcDiscoveryUnavailableError } from "~/utils/helpers/auth/oidc-contract";
import { OIDC_SESSION_KEY } from "~/utils/helpers/auth/oidc-session";
import {
  AuthSessionUnavailableError,
  LEGACY_SESSION_GENERATION_KEY,
  IMPERSONATION_SESSION_KEY,
  isAuthDocumentUnloading,
  isAuthLoggingOut,
} from "~/utils/helpers/auth/lifecycle";
import { useOidcClient } from "~/composables/auth/useOidcClient";
import { AuthModeEnum } from "~/utils/enums/auth.enums";
import { authEntryPath, isEmbeddedAuth, DOCEBO_ENTRY_PATH } from "~/utils/helpers/auth/browser-context";
import { startDoceboLogin } from "~/composables/auth/useDoceboAuth";

/** The access token of a stored Log As record: `null` without one, `undefined` when unreadable. */
function impersonationAccessToken(serialized: string | null): string | null | undefined {
  if (!serialized) return null;
  try {
    return JSON.parse(serialized)?.access_token ?? undefined;
  } catch {
    return undefined;
  }
}

export default defineNuxtPlugin({
  name: "auth-session",
  async setup() {
    const authStore = useAuthStore();
    const embeddedDoceboEntry = isEmbeddedAuth() && window.location.pathname === DOCEBO_ENTRY_PATH;
    if (embeddedDoceboEntry) startDoceboLogin();
    let restoring = false;
    let pending = false;
    let redirectOnLoss = false;
    async function reconcile(redirect = true) {
      if (isAuthDocumentUnloading() || isAuthLoggingOut()) return;
      pending = true;
      redirectOnLoss ||= redirect;
      if (restoring) return;
      restoring = true;
      try {
        do {
          pending = false;
          authStore.resetAccountContext();
          authStore.$reset();
          try {
            await authStore.restoreSession();
          } catch (error) {
            if (isAuthDocumentUnloading() || isAuthLoggingOut()) return;
            if (error instanceof OidcDiscoveryUnavailableError || error instanceof AuthSessionUnavailableError) return;
            authStore.clearLocalSession();
          }
          if (isAuthDocumentUnloading() || isAuthLoggingOut()) return;
        } while (pending);
        const redirect = redirectOnLoss;
        redirectOnLoss = false;
        if (redirect && authStore.isLoggedIn()) {
          // Mounted pages fetched data for the previous account; rebuild them from the restored session.
          reloadNuxtApp({ force: true });
        } else if (redirect && !isOidcRoute(window.location.pathname)) {
          await navigateTo(authEntryPath());
        }
      } finally {
        restoring = false;
        if (pending && !isAuthDocumentUnloading() && !isAuthLoggingOut()) void reconcile();
      }
    }
    // Subscribe before the first await: logout/account changes can arrive during profile loading.
    window.addEventListener("storage", (event) => {
      if (isEmbeddedAuth() || isAuthLoggingOut()) return;
      if (![OIDC_SESSION_KEY, LEGACY_SESSION_GENERATION_KEY, IMPERSONATION_SESSION_KEY].includes(event.key ?? ""))
        return;
      try {
        if (event.key === IMPERSONATION_SESSION_KEY) {
          // Log As started or ended in another tab: every tab runs as the same account.
          if (impersonationAccessToken(event.newValue) === (authStore.impersonation?.access_token ?? null)) return;
        } else {
          if (event.key === OIDC_SESSION_KEY && authStore.mode === AuthModeEnum.OIDC) {
            const record = useOidcClient().sessions.record();
            if (record?.session && record.generation === authStore.sessionEpoch) {
              if (!record.renewing) authStore.setOidcSession(record.session);
              return;
            }
          }
          if (authStore.mode === AuthModeEnum.IMPERSONATION) {
            if (authStore.isAdministratorSessionCurrent()) return;
            localStorage.removeItem(IMPERSONATION_SESSION_KEY);
          }
        }
        authStore.resetAccountContext();
        authStore.$reset();
        void reconcile();
      } catch {
        authStore.clearLocalSession();
      }
    });
    window.addEventListener("pageshow", (event) => {
      // A cached iframe retains its isolated in-memory session; reloading would discard it.
      if (event.persisted && !isEmbeddedAuth()) void reconcile();
    });
    if (!embeddedDoceboEntry && !isOidcRoute(window.location.pathname)) await reconcile(false);
    window.setInterval(async () => {
      if (isAuthLoggingOut() || document.visibilityState !== "visible") return;
      if (authStore.mode === AuthModeEnum.IMPERSONATION) {
        if (!authStore.isAccessTokenValid()) await authStore.exitImpersonation();
      } else if (authStore.mode === AuthModeEnum.OIDC && authStore.user) {
        try {
          await authStore.refreshToken();
        } catch (error) {
          if (error instanceof OidcDiscoveryUnavailableError || error instanceof AuthSessionUnavailableError) return;
          throw error;
        }
        if (!isAuthLoggingOut() && !authStore.isLoggedIn()) await navigateTo(authEntryPath());
      }
    }, 30_000);
  },
});
